import { fr24CsvParser } from './fr24-csv';
import { fr24KmlParser } from './fr24-kml';
import { geoJsonParser } from './geojson';
import { gpxParser } from './gpx';
import { igcParser } from './igc';
import { kmzParser } from './kmz';
import type { FormatParser, ParsedTrack, TrackFormat } from './types';
import { ParseError } from './types';

/**
 * Choosing a parser.
 *
 * Extension first, content second. Both, in that order, because each is wrong
 * on its own: a pilot who saved a KML as `flight.txt` still expects it to
 * import, and a `.csv` that is really a Garmin log should not be guessed at by
 * the KML reader just because sniffing ran first.
 *
 * Order within the list is the tie-break for sniffing, so the cheapest and
 * most specific checks come first.
 */
export const PARSERS: readonly FormatParser[] = Object.freeze([
  kmzParser,
  fr24KmlParser,
  gpxParser,
  igcParser,
  geoJsonParser,
  fr24CsvParser,
]);

/** Every extension the file picker should accept, for an `accept` attribute. */
export const ACCEPTED_EXTENSIONS: readonly string[] = Object.freeze([
  ...new Set(PARSERS.flatMap((parser) => parser.extensions)),
]);

function extensionOf(filename: string): string {
  const dot = filename.lastIndexOf('.');
  return dot === -1 ? '' : filename.slice(dot).toLowerCase();
}

/**
 * How much of a file to decode for sniffing.
 *
 * Decoding a 3.5 MB KML in full just to look at its root element is wasteful
 * when it is about to be decoded again by the parser, and 64 kB is far past
 * any of these formats' headers.
 */
const SNIFF_BYTES = 65_536;

export interface Detection {
  parser: FormatParser;
  /** How it was chosen, for the import result list. */
  by: 'extension' | 'content';
}

/**
 * Work out which parser reads this file.
 *
 * @param filename  used for the extension only; never trusted on its own.
 * @param bytes     the file's contents.
 */
export function detectFormat(filename: string, bytes: Uint8Array): Detection | undefined {
  const extension = extensionOf(filename);
  const head = new TextDecoder('utf-8', { fatal: false }).decode(bytes.subarray(0, SNIFF_BYTES));

  const byExtension = PARSERS.filter((parser) => parser.extensions.includes(extension));

  /*
   * An extension match still has to agree with the content, even when only one
   * parser claims that extension. Trusting a sole claimant outright means a
   * GPX saved as `.kml` goes to the KML reader and fails, when the GPX reader
   * was one sniff away.
   */
  for (const parser of byExtension) {
    if (parser.sniff(head)) {
      return { parser, by: 'extension' };
    }
  }

  for (const parser of PARSERS) {
    if (parser.sniff(head)) {
      return { parser, by: 'content' };
    }
  }

  // Last resort: an extension match that failed its own sniff. A parser's own
  // error message beats "unrecognised file", because it says what was missing.
  const fallback = byExtension[0];
  return fallback === undefined ? undefined : { parser: fallback, by: 'extension' };
}

/**
 * Parse a dropped file into a `ParsedTrack`.
 *
 * @throws ParseError when no parser claims the file, or when the chosen one
 *         cannot read it. The message is written to be shown to the pilot.
 */
export function parseTrackFile(filename: string, bytes: Uint8Array): ParsedTrack {
  if (bytes.length === 0) {
    throw new ParseError('This file is empty.');
  }

  const detection = detectFormat(filename, bytes);
  if (detection === undefined) {
    throw new ParseError(
      `Cannot tell what kind of file this is. Supported: ${ACCEPTED_EXTENSIONS.join(', ')}.`,
    );
  }

  const parsed = detection.parser.parse(bytes);
  return {
    ...parsed,
    notes: [
      `Read as ${detection.parser.label}${detection.by === 'content' ? ' (detected by content, not by extension)' : ''}`,
      ...parsed.notes,
    ],
  };
}

/** The formats this build can read, for an about screen or an error message. */
export function supportedFormats(): {
  format: TrackFormat;
  label: string;
  extensions: readonly string[];
}[] {
  return PARSERS.map((parser) => ({
    format: parser.format,
    label: parser.label,
    extensions: parser.extensions,
  }));
}
